import useRouter from "common/hooks/use-router";
import useList from "common/hooks/useList";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import AlertsContext from "common/providers/alerts";
import { ObservationGrid } from "types/ObservationGrid";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import usePark from "common/hooks/usePark";
import AnimalSelect from "components/select/AnimalSelect/AnimalSelect";
import ObservationGroupSelect from "components/select/ObservationGroupSelect/ObservationGroupSelect";
import UserSelect from "components/select/UserSelect/UserSelect";
import ObservationResponseItem from "./ObservationResponseItem";

function ObservationResponses() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const { park } = usePark();

  const {
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<ObservationGrid>("observation-responses/data/list", {
    defaultFilters: park?.id
      ? {
          park: park.id,
        }
      : {}, // TODO ajust
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleNavigate = (id: any) => {
    navigate(`/observation-responses/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.animal"),
        filterName: "animal",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <AnimalSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.observationGroup"),
        filterName: "observationGroup",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <ObservationGroupSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.healer"),
        filterName: "assignedUser",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <UserSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.observationResponses")}
        subtitle={t("titleLegends.observationResponses")}
        btnTitle={t("newItem.observationResponse")}
        btnSubtitle={t("newItem.observationResponseLegend")}
        Icon={ClipboardDocumentCheckIcon}
        addLinkDisabled
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={ObservationResponseItem}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default ObservationResponses;
